import { useTexture } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { GridObjectType, GridSpaceType } from 'data/types';
import { use3DRightClick } from 'hooks/use-3d-right-click';
import { DirectPointDragHandlers } from 'hooks/use-direct-point-drag';
import { useMemo } from 'react';
import { Color, MeshStandardMaterial } from 'three';
import {
  getAirHandler3DPlacement,
  getAirHandlerDimensions
} from 'utils/3d-equipment';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder
} from 'utils/3d-opacity';
import { isCeilingMountedAirHandler } from 'utils/air-handler-mounting';
import airHandlerSideTexture from './assets/air-handler-side.jpg';
import condenserTopTexture from './assets/condenser-top.jpg';
import highWallFrontTexture from './assets/high-wall-front.jpg';
import lowWallFrontTexture from './assets/low-wall-front.jpg';

type AirHandler3DProps = {
  object: GridObjectType;
  spaces?: GridSpaceType[];
  yOffset?: number;
  opacity?: number;
  selected?: boolean;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
  onRightClick?: (event: ThreeEvent<PointerEvent>) => void;
  dragHandlers?: DirectPointDragHandlers;
};

export default function AirHandler3D({
  object,
  spaces = [],
  yOffset = 0,
  opacity = 1.0,
  selected = false,
  onClick,
  onRightClick,
  dragHandlers
}: AirHandler3DProps) {
  const rightClick = use3DRightClick(onRightClick);
  const [highWallFront, lowWallFront, sideTexture, condenserTop] = useTexture([
    highWallFrontTexture,
    lowWallFrontTexture,
    airHandlerSideTexture,
    condenserTopTexture
  ]);

  const typeCode = object.airHandlerTypeCode;
  const isCeilingMounted = typeCode && isCeilingMountedAirHandler(typeCode);
  const isLowMounted = typeCode === 'heatingConsole' || typeCode === 'console';
  const frontTexture = isLowMounted ? lowWallFront : highWallFront;
  const fanFacesUp = object.concealedLocation === 'attic';
  const { width, height, depth } = getAirHandlerDimensions(typeCode);

  const placement = useMemo(
    () => getAirHandler3DPlacement(object, spaces),
    [object, spaces]
  );

  const materials = useMemo(() => {
    const createMaterial = (texture: typeof frontTexture) => {
      const texClone = texture.clone();
      texClone.needsUpdate = true;
      return new MeshStandardMaterial({
        map: texClone,
        emissive: selected ? new Color('#2563eb') : new Color('#000000'),
        emissiveIntensity: selected ? 0.35 : 0,
        ...getOpacityMaterialProps(opacity)
      });
    };

    if (isCeilingMounted) {
      return [
        createMaterial(sideTexture),
        createMaterial(sideTexture),
        createMaterial(fanFacesUp ? condenserTop : sideTexture),
        createMaterial(fanFacesUp ? sideTexture : condenserTop),
        createMaterial(sideTexture),
        createMaterial(sideTexture)
      ];
    }

    return [
      createMaterial(sideTexture),
      createMaterial(sideTexture),
      createMaterial(sideTexture),
      createMaterial(sideTexture),
      createMaterial(frontTexture),
      createMaterial(sideTexture)
    ];
  }, [
    frontTexture,
    sideTexture,
    condenserTop,
    opacity,
    selected,
    isCeilingMounted,
    fanFacesUp
  ]);

  return (
    <group
      position={[placement.x, placement.y + yOffset, placement.z]}
      rotation={[0, placement.rotation, 0]}
    >
      <mesh
        material={materials}
        renderOrder={getOpacityRenderOrder(opacity)}
        onClick={onClick}
        onPointerDown={(event) => {
          rightClick.onPointerDown(event);
          dragHandlers?.onPointerDown(event);
        }}
        onPointerMove={dragHandlers?.onPointerMove}
        onPointerUp={(event) => {
          rightClick.onPointerUp(event);
          dragHandlers?.onPointerUp(event);
        }}
        onPointerCancel={dragHandlers?.onPointerCancel}
        onPointerOver={
          dragHandlers
            ? (event) => {
                event.stopPropagation();
                document.body.style.cursor = 'grab';
              }
            : undefined
        }
        onPointerOut={
          dragHandlers
            ? () => {
                document.body.style.cursor = '';
              }
            : undefined
        }
      >
        <boxGeometry args={[width, height, depth]} />
      </mesh>
    </group>
  );
}
